import React from 'react';
type WorksheetVariant = 'ficha' | 'plan' | 'sesion';
type WorksheetMockProps = {
  variant: WorksheetVariant;
  className?: string;
};
export function WorksheetMock({
  variant,
  className = ''
}: WorksheetMockProps) {
  return <div className={`overflow-hidden rounded-md border border-beige-200 bg-paper shadow-[0_24px_40px_-24px_rgba(17,43,42,0.45)] ${className}`} aria-hidden="true" data-id="element-421" data-mp="ak0mbiyzrxoy">
      <div className="flex items-center justify-between border-b border-beige-200 px-4 pb-2.5 pt-3.5" data-id="element-422" data-mp="3e1iqngfk2mkm">
        <p className="font-serif text-[13px] uppercase tracking-wide text-petrol-800" data-id="element-423" data-mp="393h6es6e8h7k">{TITLES[variant]}</p>
        <span className="h-2 w-2 rounded-full bg-terra-500" data-id="element-424" data-mp="3n2z11o6deoou" />
      </div>
      <div className="space-y-3.5 px-4 py-3.5" data-id="element-425" data-mp="3j157dzuleky6">
        {variant === 'ficha' && <FichaBody data-id="element-426" data-mp="3j383n1mc9abc" />}
        {variant === 'plan' && <PlanBody data-id="element-427" data-mp="12byifbc81v51" />}
        {variant === 'sesion' && <SesionBody data-id="element-428" data-mp="12aoliph3uszz" />}
      </div>
    </div>;
}
const TITLES: Record<WorksheetVariant, string> = {
  ficha: 'Ficha de evaluación inicial',
  plan: 'Plan de intervención',
  sesion: 'Sesión lista'
};
function Heading({
  children,
  color = 'petrol'
}: {
  children: React.ReactNode;
  color?: 'petrol' | 'terra';
}) {
  return <p className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-wide text-petrol-800" data-id="element-429" data-mp="1flqf72991g8n">
      <span className={`h-1.5 w-1.5 rounded-full ${color === 'terra' ? 'bg-terra-500' : 'bg-petrol-700'}`} data-id="element-430" data-mp="1oklncjvut0el" />
      {children}
    </p>;
}
function Line({
  w = 'w-full'
}: {
  w?: string;
}) {
  return <span className={`block h-[5px] rounded-full bg-beige-200 ${w}`} data-id="element-431" data-mp="vvwgs5c142vg" />;
}
function CheckRow({
  label
}: {
  label?: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-432" data-mp="10tyarlaztmlp">
      <span className="h-2.5 w-2.5 shrink-0 rounded-[2px] border border-sage-500" data-id="element-433" data-mp="10swpko09jjq9" />
      {label ? <span className="text-[9px] text-ink/70" data-id="element-434" data-mp="15nspsbfnldeg">{label}</span> : <Line data-id="element-435" data-mp="85ak1a7h650r" />}
    </div>;
}
function FichaBody() {
  return <>
      <div className="space-y-1.5" data-id="element-436" data-mp="d5f1b2g7j3s1">
        <Heading data-id="element-437" data-mp="d4dfc2xnkwvj">Datos generales</Heading>
        {['Nombre', 'Edad', 'Motivo de consulta'].map(f => <div key={f} className="flex items-end gap-2" data-id="element-438" data-mp="1gfj703yssm78">
            <span className="text-[9px] text-ink/60" data-id="element-439" data-mp="1gdzo05lrbx1r">{f}:</span>
            <span className="mb-[3px] block h-px flex-1 bg-beige-300" data-id="element-440" data-mp="1bgq0ivhxhvgk" />
          </div>)}
      </div>
      <div className="space-y-1.5" data-id="element-441" data-mp="1p84kb4jnu0oo">
        <Heading color="terra" data-id="element-442" data-mp="1pah3fl15mh3b">Historia sexual</Heading>
        {['Inicio de la vida sexual', 'Experiencias previas', 'Contexto relacional'].map(l => <CheckRow key={l} label={l} data-id="element-443" data-mp="xihxq6y7nzxh" />)}
      </div>
      <div className="space-y-1.5" data-id="element-444" data-mp="xjb61cctixhq">
        <Heading data-id="element-445" data-mp="sj5c1pj2sywh">Objetivos de la consulta</Heading>
        {[1, 2, 3].map(n => <div key={n} className="flex items-center gap-2" data-id="element-446" data-mp="16i51gzeexhtp">
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-petrol-700 text-[7px] font-bold text-cream" data-id="element-447" data-mp="16jpu2jh62qox">
              {n}
            </span>
            <Line data-id="element-448" data-mp="2hwa33lr5lflv" />
          </div>)}
      </div>
    </>;
}
function PlanBody() {
  const items = ['Objetivos terapéuticos', 'Estrategias y técnicas', 'Actividades y tareas', 'Seguimiento y evaluación'];
  return <>
      {items.map((item, i) => <div key={item} className="flex gap-2.5" data-id="element-449" data-mp="2lhbzmv8bgx50" data-mpi={i}>
          <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-cream ${i === 3 ? 'bg-terra-500' : i === 2 ? 'bg-sage-700' : 'bg-petrol-700'}`} data-id="element-450" data-mp="2ph2tx8x1xvea">
            {i + 1}
          </span>
          <div className="flex-1 space-y-1 pt-0.5" data-id="element-451" data-mp="2ggegogfhfz2g">
            <p className="text-[9.5px] font-semibold text-petrol-800" data-id="element-452" data-mp="34578q1szhmrs">{item}</p>
            <Line data-id="element-453" data-mp="394trsrvhtx8i" />
            <Line w="w-4/5" data-id="element-454" data-mp="3953adue7l246" />
          </div>
        </div>)}
    </>;
}
function SesionBody() {
  return <>
      <div className="space-y-1.5" data-id="element-455" data-mp="2ugp9hp0crnq9">
        <Heading color="terra" data-id="element-456" data-mp="2ufnniphstgtr">Objetivo de la sesión</Heading>
        <CheckRow data-id="element-457" data-mp="2ze8mymp87wh5" />
        <CheckRow data-id="element-458" data-mp="2zdpdludn6geq" />
      </div>
      <div className="space-y-1.5" data-id="element-459" data-mp="2zcnsex2wwdja">
        <Heading data-id="element-460" data-mp="3n4325ax0px4k">Estructura de la sesión</Heading>
        {['Apertura', 'Desarrollo', 'Cierre'].map(l => <CheckRow key={l} label={l} data-id="element-461" data-mp="3rvtgdrz2t519" />)}
      </div>
      <div className="space-y-1.5" data-id="element-462" data-mp="3rwmfc6rsmq8u">
        <Heading data-id="element-463" data-mp="2398mwuvi2b56">Preguntas guía</Heading>
        <Line data-id="element-464" data-mp="23a9zj3s5z5p0" />
        <Line w="w-5/6" data-id="element-465" data-mp="23athgkhtdu31" />
      </div>
      <div className="space-y-1.5" data-id="element-466" data-mp="1z855i3xt071u">
        <Heading color="terra" data-id="element-467" data-mp="1z73svv153ci0">Materiales necesarios</Heading>
        <CheckRow data-id="element-468" data-mp="1u8k47mv5vmly" />
        <CheckRow data-id="element-469" data-mp="1u80omcszhab3" />
      </div>
    </>;
}